/**
 * Loads the Google Maps JavaScript SDK (Places library) exactly once per
 * page lifetime. Subsequent calls return the same promise. If the script
 * fails to load (bad key, quota, offline), the promise rejects and the
 * caller is expected to render a manual-entry fallback.
 *
 * See: docs/hub PRD §Google Maps integration.
 */

const SCRIPT_ID = 'dash-gmaps-loader';

let loaderPromise: Promise<any> | null = null;

export const getGoogleMapsApiKey = (): string | undefined =>
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export const loadGoogleMaps = (): Promise<any> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps requires a browser window'));
  }

  const w = window as any;
  if (w.google?.maps?.places && w.google.maps.geometry) {
    return Promise.resolve(w.google.maps);
  }

  if (loaderPromise) return loaderPromise;

  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    return Promise.reject(
      new Error('REACT_APP_GOOGLE_MAPS_API_KEY is not set')
    );
  }

  loaderPromise = new Promise<any>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      // Another caller started loading. Wait for it.
      existing.addEventListener('load', () => {
        if (w.google?.maps?.places && w.google.maps.geometry) {
          resolve(w.google.maps);
        } else {
          reject(
            new Error('Google Maps loaded without Places + Geometry libraries')
          );
        }
      });
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Google Maps script'))
      );
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    // language=en, region=id biases formatting and ranking toward Indonesia.
    // libraries=places (Autocomplete / PlacesService) + geometry
    // (encoding.decodePath, used by DispatchMap to render Mapbox polylines).
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
      `&libraries=places,geometry&language=en&region=ID&v=weekly`;
    script.onload = () => {
      if (w.google?.maps?.places && w.google.maps.geometry) {
        resolve(w.google.maps);
      } else {
        reject(
          new Error('Google Maps loaded without Places + Geometry libraries')
        );
      }
    };
    script.onerror = () => {
      loaderPromise = null; // allow retry on next call
      reject(new Error('Failed to load Google Maps script'));
    };
    document.head.appendChild(script);
  });

  return loaderPromise;
};

/**
 * Lightweight session-token issuer. Reusing a single token across a user's
 * autocomplete-then-details exchange keeps billing in the cheaper
 * "per-session" bucket. Caller is responsible for issuing a fresh token
 * after each `getDetails` call.
 */
export const createSessionToken = (maps: any): any =>
  new maps.places.AutocompleteSessionToken();
